import { useMemo } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import { createAgentsConfig } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';

import { areStringArraysEqual } from './areStringArraysEqual';
import { createUIConfig } from './common/utils/ui-config/createUIConfig';
import { type ConversationAssistantProps } from './types';
import { useSemanticStableValue } from './useSemanticStableValue';

type ProviderValueInputs = {
	agentsConfig?: ConversationAssistantProps['agentsConfig'];
	uiConfig?: ConversationAssistantProps['uiConfig'];
};

const areAgentsConfigsEqual = (
	current?: ConversationAssistantProps['agentsConfig'],
	next?: ConversationAssistantProps['agentsConfig'],
): boolean =>
	current?.defaultAgentId === next?.defaultAgentId &&
	areStringArraysEqual(current?.filter, next?.filter);

const getUIConfigForShallowCompare = (
	config?: ConversationAssistantProps['uiConfig'],
): Record<string, unknown> => {
	const { customHandlers: _customHandlers, elements: _elements, ...shallowConfig } = config ?? {};
	return shallowConfig;
};

const hasCustomHandlersProperty = (config?: ConversationAssistantProps['uiConfig']): boolean =>
	Object.prototype.hasOwnProperty.call(config ?? {}, 'customHandlers');

const areUIConfigsEqual = (
	current?: ConversationAssistantProps['uiConfig'],
	next?: ConversationAssistantProps['uiConfig'],
): boolean =>
	shallowEqualObjects(getUIConfigForShallowCompare(current), getUIConfigForShallowCompare(next)) &&
	shallowEqualObjects(current?.elements ?? {}, next?.elements ?? {}) &&
	hasCustomHandlersProperty(current) === hasCustomHandlersProperty(next) &&
	shallowEqualObjects(current?.customHandlers, next?.customHandlers);

export const useConversationAssistantProviderValues = ({
	agentsConfig,
	uiConfig,
}: ProviderValueInputs): {
	agentOverriderValue: ReturnType<typeof createAgentsConfig>;
	uiConfigValue: ReturnType<typeof createUIConfig>;
} => {
	const stableAgentsConfig = useSemanticStableValue(agentsConfig, areAgentsConfigsEqual);
	const stableUIConfig = useSemanticStableValue(uiConfig, areUIConfigsEqual);
	const agentOverriderValue = useMemo(
		() => createAgentsConfig(stableAgentsConfig),
		[stableAgentsConfig],
	);
	const uiConfigValue = useMemo(() => createUIConfig(stableUIConfig), [stableUIConfig]);

	return { agentOverriderValue, uiConfigValue };
};
